import type { OperatingCostCreditInstallment } from '@/types/api'
import {
  formatRateInputValue,
  parseOptionalAmountInput,
  parseOptionalRateInput,
  parseRateInput,
} from '../lib/operating-costs'

export type CreditInstallmentDraft = {
  installment: number
  rate: string
  surchargeRate: string
  surchargeAmount: string
}

export function emptyCreditInstallmentDrafts(): CreditInstallmentDraft[] {
  return Array.from({ length: 12 }, (_, index) => ({
    installment: index + 1,
    rate: '',
    surchargeRate: '',
    surchargeAmount: '',
  }))
}

export function draftsFromCreditInstallments(
  installments: OperatingCostCreditInstallment[]
): CreditInstallmentDraft[] {
  const byInstallment = new Map(installments.map((item) => [item.installment, item]))
  return emptyCreditInstallmentDrafts().map((draft) => {
    const current = byInstallment.get(draft.installment)
    if (!current) return draft
    return {
      installment: current.installment,
      rate: formatRateInputValue(current.rate),
      surchargeRate: current.surcharge_rate === 0 ? '' : formatRateInputValue(current.surcharge_rate),
      surchargeAmount: current.surcharge_amount === 0 ? '' : String(current.surcharge_amount),
    }
  })
}

export function parseCreditInstallmentDrafts(
  drafts: CreditInstallmentDraft[]
): { installments: { installment: number; rate: number; surchargeRate: number; surchargeAmount: number }[] } | string {
  const installments = []
  for (const draft of drafts) {
    const rate = parseRateInput(draft.rate)
    if (rate === null) {
      return `Informe a taxa da parcela ${draft.installment}x.`
    }
    const surchargeRate = parseOptionalRateInput(draft.surchargeRate)
    if (surchargeRate === null) {
      return `Informe um acréscimo percentual válido na parcela ${draft.installment}x.`
    }
    const surchargeAmount = parseOptionalAmountInput(draft.surchargeAmount)
    if (surchargeAmount === null) {
      return `Informe um acréscimo em reais válido na parcela ${draft.installment}x.`
    }
    installments.push({
      installment: draft.installment,
      rate,
      surchargeRate,
      surchargeAmount,
    })
  }
  return { installments }
}

export function creditDefaultFormKey(
  defaultFees: { installments: OperatingCostCreditInstallment[] } | null
): string {
  if (!defaultFees) return 'empty'
  return defaultFees.installments
    .map(
      (item) =>
        `${item.installment}:${item.rate}:${item.surcharge_rate}:${item.surcharge_amount}`
    )
    .join('|')
}
